<template>
    <section class="image-bg-title-section">
        <div class="image-bg-title-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />

            <div class="image-bg-title__grid">
                <article v-for="(cell, idx) in item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="image-bg-title__card"
                    @click="handleCellClick(cell)">
                    <!-- Background image (faded) -->
                    <div v-if="getCellBgImage(cell)" class="image-bg-title__bg">
                        <img :src="getCellBgImage(cell)" :alt="getCellTitle(cell)" loading="lazy" />
                    </div>
                    <!-- White overlay to fade bg -->
                    <div class="image-bg-title__overlay" />

                    <!-- Content -->
                    <div class="image-bg-title__content">
                        <h3 v-if="getCellTitle(cell)" class="image-bg-title__title">
                            {{ getCellTitle(cell) }}
                        </h3>
                        <p v-if="getCellDesc(cell)" class="image-bg-title__desc">
                            {{ getCellDesc(cell) }}
                        </p>
                    </div>
                    <a v-if="getCellLinkText(cell)" class="image-bg-title__link" @click.stop="handleCellClick(cell)">
                        {{ getCellLinkText(cell) }}
                        <span class="arrow">›</span>
                    </a>

                    <!-- Icon at bottom-left -->
                    <div v-if="getCellIcon(cell)" class="image-bg-title__icon">
                        <img v-if="isImageIcon(cell)" :src="getCellIcon(cell)" alt="" />
                        <span v-else class="icon-text">{{ getCellIcon(cell) }}</span>
                    </div>
                </article>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}

function getCellDesc(cell: unknown): string {
    return getStrWithRecord(cell, 'content')
}

function getCellBgImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}

function getCellIcon(cell: unknown): string {
    return getStrWithRecord(cell, 'icon')
}

function getCellLinkText(cell: unknown): string {
    return getStrWithRecord(cell, 'urlText')
}

function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}


function isImageIcon(cell: unknown): boolean {
    const icon = getCellIcon(cell)
    return /^(https?:|\/|\.)/i.test(icon) || icon.endsWith('.svg') || icon.endsWith('.png') || icon.endsWith('.jpg')
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) {
        jumpToUrl(url)
    }
}


</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$link-color: #2563eb;

.image-bg-title-section {
    width: 100%;
}

.image-bg-title-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.image-bg-title__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;

    @media (min-width: $breakpoint-md) {
        grid-template-columns: repeat(4, 1fr);
        gap: 1.25rem;
    }
}

.image-bg-title__card {
    position: relative;
    min-height: 200px;
    padding: 1.25rem;
    border-radius: 20px;
    background: #f0f0f3;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    flex-direction: column;

    @media (min-width: $breakpoint-md) {
        min-height: 280px;
        padding: 1.75rem;
        border-radius: 24px;
    }

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    }
}

.image-bg-title__bg {
    position: absolute;
    inset: 0;
    z-index: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        opacity: 0.35;
        filter: grayscale(0.1);
    }
}

.image-bg-title__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(180deg,
            rgba(240, 240, 243, 0.92) 0%,
            rgba(240, 240, 243, 0.75) 50%,
            rgba(240, 240, 243, 0.55) 100%);
}

.image-bg-title__content {
    flex: 1;
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;

    @media (min-width: $breakpoint-md) {
        gap: 0.75rem;
    }
}

.image-bg-title__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 800;
    color: #1a1a1a;
    line-height: 1.2;

    @media (min-width: $breakpoint-md) {
        font-size: 1.75rem;
    }
}

.image-bg-title__desc {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 400;
    color: #555;
    line-height: 1.5;

    @media (min-width: $breakpoint-md) {
        font-size: 0.9375rem;
    }
}

.image-bg-title__link {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;

    font-size: 0.875rem;
    font-weight: 600;
    color: $link-color;
    cursor: pointer;
    transition: color 0.15s;
    padding-top: 0.75rem;

    @media (min-width: $breakpoint-md) {
        font-size: 1rem;
        padding-top: 1rem;
    }

    .arrow {
        font-size: 1.1em;
        font-weight: 700;
        transition: transform 0.15s;
    }

    &:hover {
        color: #1d4ed8;

        .arrow {
            transform: translateX(2px);
        }
    }
}

.image-bg-title__icon {
    position: relative;
    margin-top: 0.2rem;
    // position: absolute;
    // left: 1.25rem;
    // bottom: 1rem;
    z-index: 2;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #888;
    opacity: 0.7;

    @media (min-width: $breakpoint-md) {
        // left: 1.75rem;
        // bottom: 1.25rem;
        width: 3rem;
        height: 3rem;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .icon-text {
        font-size: 2.5rem;
        font-weight: 300;

        @media (min-width: $breakpoint-md) {
            font-size: 3rem;
        }
    }
}
</style>
